import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { TouchableOpacity, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { AppView, Button, ButtonGroup, CardView, Spacer } from 'design-system';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import CardRow from 'design-system/CardRow';
import { LineSeparator } from 'design-system/LineSeparator';
import CardUpgradeRow, { cardUpgradeRowHeight } from 'design-system/CardUpgradeRow';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import { IconRocket } from 'design-system/icons/IconRocket';
import IconTooltip from 'design-system/icons/IconTooltip';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Squircle from 'design-system/Squircle';
import Text from 'design-system/Text';
import { hitSlopS, rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { muteErrorAndResult, useFetchOld } from 'hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { formatAmount, getOrdinal } from 'utils/formatting';
import { goBackToTopWithStops } from 'utils/navigationv6';
import Amplitude from 'utils/amplitude';
import CardRowContainer from 'design-system/CardRowContainer';
import AutoInvestFeeInfoModal from '../components/AutoInvestFeeInfoModal';
import { acceptAutoInvestMandate, createAutoInvestPlan } from '../actions/autoinvest';
import { selectActiveTradingAccountId, selectAutoInvestFee } from '../reducers/selectors';
import { useConnectedAccount } from '../hooks/selectors';
import { isWeb } from '../../../constants';
const AutoinvestReviewScreen = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const { collectionDate, amount, allocations } = route.params;
    const [, , loadingAction, fetch] = useFetchOld(muteErrorAndResult);
    const accountId = useAppSelector(selectActiveTradingAccountId);
    const currentFee = useAppSelector(selectAutoInvestFee);
    const connectedAccount = useConnectedAccount();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
        });
    }, []);
    const collectionDateString = useMemo(() => `${getOrdinal(collectionDate)} of each month`, [collectionDate]);
    const showFeeModal = useCallback(() => {
        if (typeof currentFee === 'number') {
            AutoInvestFeeInfoModal.show({ value: currentFee, currency: 'GBP' });
        }
    }, [currentFee]);
    const isFixFlow = !!route.params.planId;
    const handleConfirm = useCallback(async () => {
        if (!accountId || !connectedAccount)
            return;
        const mandateResult = await fetch(acceptAutoInvestMandate(connectedAccount.ukSortCode.replace(/-/g, ''), connectedAccount.ukAccountNumber));
        if (!mandateResult)
            return;
        if (!isFixFlow) {
            const result = await fetch(createAutoInvestPlan({
                accountId,
                amount,
                dayOfMonth: collectionDate,
                allocations,
            }));
            if (!result)
                return;
        }
        Amplitude.logEvent('Submitted Request', {
            type: 'autoinvest',
        });
        navigation.navigateDeprecated('Success', {
            title: isFixFlow ? 'Direct Debit reconnected' : 'Autoinvest has been enabled',
            subTitle: isFixFlow
                ? 'Your Direct Debit has been reconnected. Your Autoinvest will resume as normal.'
                : 'Your Direct Debit has been set up. Emma will start investing for you automatically. You can pause this at any time.',
            buttonText: 'Done',
            onContinue: () => {
                goBackToTopWithStops(navigation, ['Report', 'InvestPortfolio']);
                return true;
            },
        });
    }, [accountId, connectedAccount, isFixFlow, amount, collectionDate, allocations]);
    return (<AppView type="secondary" withFrame={false}>
        <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <ScreenInfoHeader title="Review and confirm"/>
          <CardView noMarginTop>
            <View style={styles.cardHeader}>
              <View style={styles.flex}>
                <Text Text-16>Autoinvest</Text>
              </View>
              <Squircle width={rem(44)} height={rem(44)} color={colors.background.dark}>
                <IconRocket color={colors.text.investment}/>
              </Squircle>
            </View>
            <LineSeparator noMargin/>
            <CardRowContainer>
              <CardRow title="Collection date" info={collectionDateString} small smallPadding/>
              <CardRow title="Account" info={connectedAccount?.bankName || 'Linked bank'} small smallPadding/>
              {currentFee !== undefined && (<CardRow title={<View style={styles.row}>
                      <Text TextThin-14 Secondary>
                        Autoinvest fee
                      </Text>
                      <Spacer w={4}/>
                      <View>
                        <Spacer h={4}/>
                        <TouchableOpacity hitSlop={hitSlopS} onPress={showFeeModal}>
                          <IconTooltip height={14} width={14} colorKey="secondary"/>
                        </TouchableOpacity>
                      </View>
                    </View>} info={<Text Numbers-14>{formatAmount(currentFee, 'GBP', 2)}</Text>} small smallPadding/>)}
              <CardRow title="Amount" info={<Text Numbers-14>{formatAmount(amount, 'GBP', 2)}</Text>} small smallPadding/>
            </CardRowContainer>

            {!!currentFee && (<>
                <CardUpgradeRow title="Want no fee?" withHorizontalMargins/>
                <Spacer.H16 />
              </>)}
          </CardView>
        </Animated.ScrollView>
        <ButtonGroup type="floating">
          <Text TextThin-16 Secondary centered>
            Capital at risk
          </Text>
          <View />
          <Button brand title="Confirm" onPress={handleConfirm} loading={loadingAction}/>
        </ButtonGroup>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets((colors) => ({
    cardHeader: {
        padding: rem(16),
        alignItems: 'center',
        flexDirection: 'row',
    },
    flex: {
        flex: 1,
    },
    row: {
        flex: 1,
        flexDirection: 'row',
    },
    upgradeRow: {
        alignItems: 'center',
        borderRadius: rem(8),
        flexDirection: 'row',
        paddingHorizontal: rem(16),
        marginHorizontal: rem(16),
        height: cardUpgradeRowHeight,
        backgroundColor: colors.buttons.brandGroup,
    },
}));
export default AutoinvestReviewScreen;
